import React from 'react';
import { compose } from 'recompose';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { ApiResponse, MailingList, Exchange } from 'constants/api';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ViewContainer from 'components/utils/ViewContainer';
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import { DetailsTable } from 'components/styled';
import RequestStatusRenderer from 'components/utils/RequestStatusRenderer';
import PageLoading from 'components/utils/PageLoading';
import AlertMessage from 'components/utils/AlertMessage';
import NotFound from './NotFound';
import ExchangeWithFlag from 'components/utils/Exchange';
import { connect } from 'react-redux';
import { ReduxState } from 'createStore';
import { getExchangeByUuidSelector } from 'redux/apiData/apiDataSelectors';
import MailingListEditModal from 'components/modals/MailingListEditModal';
import { EMAIL_LIST_DELIMITER } from 'constants/index';

interface DetailsInProps {
    mailingList: MailingList;
    refetchData: () => void;
}

interface DetailsEnhanceProps {
    exchange: Exchange;
}

type DetailsProps = DetailsInProps & DetailsEnhanceProps;

const enhanceDetails = compose<DetailsProps, DetailsInProps>(
    connect((state: ReduxState, props: DetailsProps) => ({
        exchange: getExchangeByUuidSelector(state.apiData, props.mailingList.exchange_uuid),
    }))
);

function MailingListDetails(props: DetailsProps) {
    const { t } = useTranslation();
    const { mailingList, exchange, refetchData } = props;
    const pageTitle = t('mailingListDetails.pageTitle', { name: mailingList.name });

    return (
        <>
            <Helmet title={pageTitle} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{pageTitle}</h2>
                                <SyncButton
                                    className="ml-2"
                                    title={t('mailingListDetails.refreshButton')}
                                    onClick={refetchData}
                                />
                            </div>
                            <MailingListEditModal mailingList={mailingList} />
                        </div>
                        <hr />
                        <Card className="card-accent-primary">
                            <CardHeader>
                                <strong>{t('mailingListDetails.detailsTitle')}</strong>
                            </CardHeader>
                            <CardBody>
                                <DetailsTable size="sm" borderless>
                                    <tbody>
                                        <tr>
                                            <th>{t('common.exchange')}</th>
                                            <td>
                                                <ExchangeWithFlag shortName={exchange.short_name} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{t('mailingList.name')}</th>
                                            <td>{mailingList.name}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('mailingList.description')}</th>
                                            <td>{mailingList.description}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('mailingList.listUrl')}</th>
                                            <td>
                                                {mailingList.list_url && (
                                                    <a href={mailingList.list_url} target="_blank">
                                                        {mailingList.list_url}
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{t('mailingLists.archives')}</th>
                                            <td>
                                                {mailingList.archives_url && (
                                                    <a href={mailingList.archives_url} target="_blank">
                                                        {mailingList.archives_url}
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{t('mailingList.contactEmail')}</th>
                                            <td>
                                                {mailingList.contact_email &&
                                                    mailingList.contact_email
                                                        .split(EMAIL_LIST_DELIMITER)
                                                        .map((email) => {
                                                            const sanitizedEmail = email.trim();
                                                            return (
                                                                <span key={sanitizedEmail} className="email-list-item">
                                                                    <a href={`mailto:${sanitizedEmail}`}>{sanitizedEmail}</a>
                                                                </span>
                                                            );
                                                        })}
                                            </td>
                                        </tr>
                                    </tbody>
                                </DetailsTable>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
}

const EnhancedMailingListDetails = enhanceDetails(MailingListDetails);

interface WrapperInProps {
    uuid: string;
}

interface WrapperEnhanceProps {
    mailingListBinding: ApiDataBinding<ApiResponse<MailingList>>;
}

type WrapperProps = WrapperInProps & WrapperEnhanceProps;

const enhance = compose<WrapperProps, WrapperInProps>(
    withApiData(
        {
            mailingListBinding: 'getMailingList',
        },
        (props: WrapperInProps) => ({
            mailingListBinding: { uuid: props.uuid },
        })
    )
);

function Wrapper(props: WrapperProps) {
    const { mailingListBinding } = props;
    const { t } = useTranslation();

    return (
        <RequestStatusRenderer
            request={mailingListBinding}
            loading={<PageLoading />}
            success={() => (
                <EnhancedMailingListDetails
                    mailingList={mailingListBinding.data!.data!}
                    refetchData={mailingListBinding.invalidateCache}
                />
            )}
            failed={() => {
                if (mailingListBinding.request.response!.status === 404) {
                    return <NotFound />;
                } else {
                    return <AlertMessage message={t('notification.unexpectedError')} />;
                }
            }}
        />
    );
}

export default enhance(Wrapper);
